import { useGlobal } from '@/context/GlobalContext';
import { usePageContent } from '@/hooks/usePageContent';
import { SampleProducts, ProductCard } from '@/components/SampleProducts';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO, { serviceSchema } from '@/components/SEO';
import { 
  Calendar, Shield, Camera, Video, Server, Package,
  CheckCircle, Building2, ShieldCheck, DollarSign, Cog, ArrowRight
} from 'lucide-react';

const SurveillancePage = () => {
  const { settings } = useGlobal();
  const { content, loading: contentLoading } = usePageContent('surveillance');

  // Get content with defaults
  const hero = content.hero || {};
  const whatWeOffer = content.what_we_offer || {};
  const whyWorkWithUsCMS = content.why_work_with_us || {};
  const whoWeWorkWithCMS = content.who_we_work_with || {};
  const customSolutions = content.custom_solutions || {};
  const cta = content.cta || {};

  // Default values
  const defaults = {
    hero: {
      badge: "SOLUTIONS",
      headline: "Surveillance Systems — Sourced Right",
      description: "Access to quality factories, NDAA-compliant options, and custom solutions when you need them.",
      badges: ["IP & Analog", "NDAA-Compliant", "Private Label Ready"],
      background_image: "https://images.unsplash.com/photo-1672073311074-f60c4a5e7b92?w=1200&q=80"
    },
    what_we_offer: {
      title: "What We Offer",
      description: "A full range of surveillance products sourced from our vetted factory network.",
      categories: [
        { icon: 'Camera', title: 'IP Cameras', description: 'Network cameras for modern surveillance infrastructure' },
        { icon: 'Video', title: 'Analog Cameras', description: 'Traditional CCTV for legacy and hybrid systems' },
        { icon: 'Server', title: 'NVRs & DVRs', description: 'Recording solutions for any scale deployment' },
        { icon: 'Package', title: 'Complete Kits', description: 'Turnkey packages for quick deployment' }
      ]
    },
    why_work_with_us: {
      title: "Why Work With Us",
      description: "More than a supplier — a sourcing partner who understands your needs.",
      items: [
        { icon: 'ShieldCheck', title: 'Vetted Factory Network', description: 'We work with quality manufacturers we know and trust. No guesswork on your supply chain.' },
        { icon: 'Shield', title: 'NDAA-Compliant Options', description: 'For government, critical infrastructure, and regulated sectors — compliant products available.' },
        { icon: 'Building2', title: 'Custom Configurations', description: 'Specific environments need specific solutions. We configure systems to match your requirements.' },
        { icon: 'DollarSign', title: 'Competitive Pricing', description: 'Direct factory relationships mean better pricing without compromising on quality.' }
      ]
    },
    who_we_work_with: {
      title: "Who We Work With",
      customers: [
        { title: 'System Integrators', description: 'Need specific cameras for a project? We source the right products to match your specs — quantities large or small.', cta: 'Discuss Your Project' },
        { title: 'Distributors', description: 'Build your brand with private-label ready products. Competitive pricing, reliable supply, your branding.', cta: 'Partnership Inquiry' }
      ]
    },
    custom_solutions: {
      badge: "CUSTOM SOLUTIONS",
      title: "Off-the-Shelf Doesn't Fit?",
      description: "Sometimes standard products won't meet your requirements. When you need something tailored — specific sensor configurations, custom housings, or unique ISP tuning — we have the engineering capability to design it.",
      features: ["Custom sensor & ISP configurations", "Special environment adaptations", "OEM/ODM development"]
    },
    cta: {
      title: "Tell Us What You're Looking For",
      description: "Whether you need a specific product for a project or want to explore a distribution partnership — let's talk."
    }
  };

  // Merge CMS content with defaults
  const h = { ...defaults.hero, ...hero };
  const wo = { ...defaults.what_we_offer, ...whatWeOffer };
  const wwu = { ...defaults.why_work_with_us, ...whyWorkWithUsCMS };
  const wwww = { ...defaults.who_we_work_with, ...whoWeWorkWithCMS };
  const cs = { ...defaults.custom_solutions, ...customSolutions };
  const c = { ...defaults.cta, ...cta };

  const productCategories = wo.categories || defaults.what_we_offer.categories;
  const whyWorkWithUs = wwu.items || defaults.why_work_with_us.items;
  const customerTypes = wwww.customers || defaults.who_we_work_with.customers;

  // Icon mapping
  const iconMap = { Camera, Video, Server, Package, ShieldCheck, Shield, Building2, DollarSign };

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="surveillance-page">
      <SEO 
        title="Surveillance Systems - NDAA Compliant Cameras & NVRs"
        description="Surveillance camera sourcing partner. IP cameras, analog CCTV, NVRs, DVRs. NDAA-compliant options, private label ready, competitive factory pricing. Custom configurations available."
        keywords="NDAA compliant camera, surveillance camera supplier, IP camera distributor, NVR system, CCTV manufacturer, security camera ODM, private label CCTV, CCTV IPC, IPC camera, wholesale surveillance camera, CCTV distributor, wholesale security camera"
        canonicalUrl="https://www.trinity-tw.com/surveillance"
        ogImage="https://images.unsplash.com/photo-1672073311074-f60c4a5e7b92?w=1200&q=80"
        structuredData={serviceSchema('Surveillance Systems', 'Surveillance camera sourcing with NDAA-compliant options, IP and analog cameras, NVRs, and custom configurations.')}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/30 via-[#1a1f2e]/50 to-[#1a1f2e]/60" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#1e2530] border border-[#3d4758] text-sm text-slate-300 mb-6">
            {h.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            {h.headline}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {h.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {(h.badges || []).map((badge, idx) => (
              <span key={idx} className="px-4 py-2 rounded-full bg-transparent border border-[#3d4758] text-sm text-slate-400">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {wo.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {wo.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((category, idx) => {
              const IconComponent = iconMap[category.icon] || Camera;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-6 text-center hover:border-[#5b3589] transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-6 h-6 text-[#8b5cf6]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 font-['Montserrat']">
                    {category.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{category.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Products */}
      <SampleProducts
        endpoint="camera-modules"
        title="Sample Surveillance Products"
        subtitle="Representative examples from our sourcing network. NDAA-compliant and custom options available."
        limit={4}
        renderCard={(item) => (
          <ProductCard
            key={item.id}
            image={item.main_image}
            title={item.name}
            description={item.short_description}
            tags={item.key_features?.split('\n').slice(0, 2) || []}
            Icon={Camera}
          />
        )}
      />

      {/* Why Work With Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {wwu.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {wwu.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyWorkWithUs.map((item, idx) => {
              const IconComponent = iconMap[item.icon] || ShieldCheck;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#5b3589]/20 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[#8b5cf6]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2 font-['Montserrat']">
                        {item.title}
                      </h3>
                      <p className="text-slate-400">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Customer Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {wwww.title}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {customerTypes.map((type, idx) => (
              <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                <h3 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">
                  {type.title}
                </h3>
                <p className="text-slate-400 mb-6">{type.description}</p>
                <Link to="/contact">
                  <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full">
                    {type.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When You Need More - Design Capability */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-r from-[#5b3589]/20 to-[#151b23] border border-[#3d4758] p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#5b3589]/30 text-xs text-[#a78bfa] font-medium mb-4">
                  {cs.badge}
                </div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
                  {cs.title}
                </h2>
                <p className="text-slate-400 mb-6">
                  {cs.description}
                </p>
                <div className="space-y-3 mb-8">
                  {(cs.features || defaults.custom_solutions.features).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-[#8b5cf6]" />
                      {feature}
                    </div>
                  ))}
                </div>
                <Link to="/engineering">
                  <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full">
                    <Cog className="w-4 h-4 mr-2" />
                    Learn About Our Engineering Services
                  </Button>
                </Link>
              </div>
              <div className="flex justify-center">
                <div className="w-48 h-48 rounded-full bg-[#5b3589]/10 flex items-center justify-center">
                  <Cog className="w-24 h-24 text-[#5b3589]/40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
            {c.title}
          </h2>
          <p className="text-slate-400 mb-8">
            {c.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Call
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Send an Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SurveillancePage;
